<template>
  <div class="tree-panel">
    <div class="tree-panel-header">
      <div class="tree-panel--nav">
        <template v-for="(menu, index) in navMenu">
          <div :key="menu.id" class="nav-item" :class="{'is-active': index === navMenu.length - 1}" @click="handleBack(menu, index)">
            <span class="nav-item--name">{{ menu.name }}</span>
            <x-svg-icon v-if="index !== navMenu.length - 1" name="arrow-right-icon"></x-svg-icon>
          </div>
        </template>
      </div>
    </div>
    <div class="tree-panel-body">
      <div class="tree-panel--department">
        <template v-for="department in departmentData">
          <department-option
            :key="department.id"
            :department="department"
            :selectType="selectType"
            @click.native="handleClick(department)"
          >
            <template v-slot:suffix>
              <div v-if="!department.leafNodeFlag" class="tree-panel--action" @click.stop="handleGo(department)">
                <x-svg-icon name="arrow-right-icon"></x-svg-icon>
              </div>
            </template>
          </department-option>
        </template>
        <template v-if="departmentData.length === 0">
          <x-empty></x-empty>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import DepartmentOption from '../department-option/department-option'
import { mapState, mapMutations } from 'vuex'
import apis from '@/api'
import {
  PUSH_NAV_MENU,
  POP_NAV_MENU
} from '@/store/department.store.js'

export default {
  components: {
    DepartmentOption
  },
  props: {
    selectType: {
      type: String,
      default: 'SINGLE' // MULTI
    },
    canSelectAll: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      departmentData: []
    }
  },
  computed: {
    ...mapState({
      navMenu: state => state.departmentModule.navMenu,
      selectedArray: state => state.departmentModule.selectedArray
    }),
    isAllSelected() {
      return this.departmentData.some(item => this.selectedArray.findIndex(_item => _item.id === item.id) === -1)
    }
  },
  watch: {
    'selectedArray': {
      handler(newVal, oldVal) {
        this.updateDepartmentData(newVal)
      }
    },
    '$route.params.id': {
      handler(newVal, oldVal) {
        if (newVal !== 'all') {
          this.queryChildDepartment(newVal).then((data) => {
            this.init(data)
          })
        } else {
          this.queryTopDepartment().then(data => {
            this.init(data)
          })
        }
      },
      immediate: true
    },
    'isAllSelected': {
      handler(newVal, oldVal) {
        this.$emit('update:canSelectAll', newVal)
      },
      immediate: true
    }
  },
  activated() {
    this.$emit('update:canSelectAll', this.isAllSelected)
  },
  methods: {
    ...mapMutations('departmentModule', {
      pushNavMenu: PUSH_NAV_MENU,
      popNavMenu: POP_NAV_MENU
    }),
    init(data) {
      this.departmentData = data.map(item => {
        const index = this.selectedArray.findIndex(_item => _item.id === item.id)
        if (index !== -1) {
          item.checked = true
        } else {
          item.checked = false
        }
        return item
      })
    },
    queryTopDepartment() {
      this.$loading.show()
      return new Promise((resolve, reject) => {
        const request = {
          ...apis.COMPONENT.QUERY_TOP_DEPARTMENT
        }
        this.$request(request).asyncThen((resp) => {
          this.$loading.hide()
          if (resp.code === 'ok') {
            resolve(resp.data)
          }
        }, (err) => {
          this.$loading.hide()
        }).asyncErrorCatch(err => {
          this.$loading.hide()
        })
      })
    },
    queryChildDepartment(parentId) {
      this.$loading.show()
      return new Promise((resolve, reject) => {
        const request = {
          ...apis.COMPONENT.QUERY_CHILD_DEPARTMENT,
          params: {
            parentId
          }
        }
        this.$request(request).asyncThen((resp) => {
          this.$loading.hide()
          if (resp.code === 'ok') {
            resolve(resp.data)
          }
        })
      }, (err) => {
        this.$loading.hide()
      }).asyncErrorCatch(err => {
        this.$loading.hide()
      })
    },
    handleClick(department) {
      department.checked = !department.checked
      this.$emit('select', department)
    },
    handleBack(department, index) {
      if (index !== this.navMenu.length - 1) {
        const step = this.navMenu.length - index - 1
        for (let i = 0; i < step - 1; i++) {
          this.popNavMenu()
        }
        this.$router.go(-step)
      }
    },
    handleGo(department) {
      this.pushNavMenu(department)
      this.$router.push({
        name: 'FormDepartmentSelect',
        params: {
          id: department.id
        },
        query: this.$route.query
      })
    },
    updateDepartmentData(selectedArray) {
      this.departmentData.forEach(item => {
        const index = selectedArray.findIndex(_item => _item.id === item.id)
        item.checked = index !== -1
      })
    },
    handleSelectAll(checked) {
      this.departmentData.forEach(department => {
        department.checked = checked
        this.$emit('select', department)
      })
    }
  }
}
</script>

<style lang="scss">
.tree-panel {
  .tree-panel-header {
    display: flex;
    align-items: center;
    padding: 0 1rem;
    height: 2.86rem;
    border-bottom: 1px solid $--app-info-color;
    background-color: $--app-box-bgColor;
    .tree-panel--nav {
      display: flex;
      align-items: center;
      overflow-x: auto;
      overflow-y: hidden;
      white-space: nowrap;
      .nav-item {
        display: flex;
        align-items: center;
        font-size: 0;
        .nav-item--name {
          font-size: $--app-title-font-size;
          color: $--app-primary-color;
        }
        &.is-active {
          .nav-item--name {
            color: $--app-notice-font-color;
          }
        }
      }
    }
  }
  .tree-panel-body {
    .tree-panel--department {
      .tree-panel--action {
        flex: 0 0 3.43rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0;
        color: $--app-notice-color;
      }
      .x-empty {
        margin: 40% auto;
      }
    }
  }
}
</style>
